/**
 * Landing Page Component
 *
 * Created from HTML: cat_landing_page_-_yellow_theme/code.html (lines 72-325)
 * Exact conversion - every class, structure, and content matches HTML
 */

import { Link } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@vsat/shared/components';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { SEOHead } from '../components/SEOHead';
import { getCanonicalUrl } from '../utils/siteConfig';
import { CAT_FAQ } from '../data/faq';
import { TESTIMONIALS } from '../data/testimonialsData';
import { injectFAQStructuredData } from '../utils/faqStructuredData';
import { StructuredData } from '../components/StructuredData';
import { trackCTAClick, initScrollDepthTracking } from '../telemetry/googleAnalytics';
import {
  Palette,
  Users,
  FlaskConical,
  Wrench,
  Briefcase,
  Calculator,
  Brain,
  BookOpen,
  GraduationCap,
  FileText,
} from 'lucide-react';

// Lazy load FAQ section to reduce main bundle size
const FAQSection = lazy(() =>
  import('../components/FAQSection').then(module => ({ default: module.FAQSection }))
);

// Lazy load Footer to reduce initial bundle size
const UnifiedFooter = lazy(() =>
  import('../components/UnifiedFooter').then(module => ({ default: module.UnifiedFooter }))
);

// Demo data for Holland radar chart - matching Compass.tsx exactly
// RIASEC order with angles matching Compass.tsx
const ORDERED_CODES = ['R', 'I', 'A', 'S', 'E', 'C'] as const;

const hollandConfig: Record<
  string,
  {
    icon: any;
    color: string;
    label: string;
    angle: number; // in degrees, -90 is top
  }
> = {
  R: { icon: Wrench, color: '#dc2626', label: 'Kỹ thuật', angle: -90 }, // Top
  I: { icon: FlaskConical, color: '#0369A1', label: 'Nghiên cứu', angle: -30 }, // Top Right
  A: { icon: Palette, color: '#F97316', label: 'Nghệ thuật', angle: 30 }, // Bottom Right
  S: { icon: Users, color: '#16a34a', label: 'Xã hội', angle: 90 }, // Bottom
  E: { icon: Briefcase, color: '#9333ea', label: 'Quản lý', angle: 150 }, // Bottom Left
  C: { icon: Calculator, color: '#6b7280', label: 'Nghiệp vụ', angle: 210 }, // Top Left
};

const maxScore = 8;

// Generate random scores in range 2-6
const generateRandomScores = (): Record<string, number> => {
  const scores: Record<string, number> = {};
  ORDERED_CODES.forEach(code => {
    // Random integer between 2 and 6 (inclusive)
    scores[code] = Math.floor(Math.random() * 5) + 2;
  });
  return scores;
};

// Identify top 3 groups by score
const getTop3Codes = (scores: Record<string, number>) => {
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return new Set(sorted.slice(0, 3).map(([code]) => code));
};

// Holland Radar Chart Component - Matching Compass.tsx exactly
function HollandRadarChart({ scores }: { scores: Record<string, number> }) {
  // Responsive size: smaller on mobile, larger on desktop
  const [size, setSize] = useState(500);

  useEffect(() => {
    const updateSize = () => {
      // Mobile: use viewport width minus padding (320px min, 400px max)
      // Tablet: 450px
      // Desktop: 500px
      if (window.innerWidth < 640) {
        const padding = 48; // px-6 (24px each side)
        const calculatedSize = Math.min(Math.max(window.innerWidth - padding, 280), 400);
        setSize(calculatedSize);
      } else if (window.innerWidth < 1024) {
        setSize(450);
      } else {
        setSize(500);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const center = size / 2;
  const scaleFactor = size / 550; // Scale relative to Compass.tsx 550
  const iconRadius = 160 * scaleFactor; // Radius for icons
  const chartRadius = 110 * scaleFactor; // Radius for score polygon (max score)
  const top3Codes = getTop3Codes(scores);

  // Helper to get coordinates (matching Compass.tsx)
  const getCoords = (angleDeg: number, r: number) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: center + r * Math.cos(angleRad),
      y: center + r * Math.sin(angleRad),
    };
  };

  // Generate polygon points for scores (matching Compass.tsx)
  const scorePolygonPoints = ORDERED_CODES.map(code => {
    const score = scores[code] || 0;
    const ratio = Math.min(score / maxScore, 1);
    const r = chartRadius * ratio;
    const { x, y } = getCoords(hollandConfig[code].angle, r);
    return `${x},${y}`;
  }).join(' ');

  // Generate hexagon background points (Web Grid) - matching Compass.tsx
  const getHexagonPoints = (rRatio: number) => {
    return ORDERED_CODES.map(code => {
      const { x, y } = getCoords(hollandConfig[code].angle, chartRadius * rRatio);
      return `${x},${y}`;
    }).join(' ');
  };

  return (
    <div className='w-full flex items-center justify-center py-4'>
      <div
        className='relative flex items-center justify-center font-sans select-none'
        style={{ width: size, height: size }}
      >
        {/* SVG Layer for Chart */}
        <svg width={size} height={size} className='absolute inset-0 z-0 pointer-events-none'>
          {/* Radar Grids (Concentric Hexagons) - matching Compass.tsx */}
          {[0.25, 0.5, 0.75, 1].map((ratio, i) => (
            <polygon
              key={i}
              points={getHexagonPoints(ratio)}
              fill='none'
              stroke='#E5E7EB'
              strokeWidth={1}
              strokeDasharray={i === 3 ? '0' : '4 4'}
              className='dark:stroke-gray-700'
            />
          ))}

          {/* Axis Lines with animated pulses for top 3 - matching Compass.tsx */}
          {ORDERED_CODES.map((code, idx) => {
            const { x, y } = getCoords(hollandConfig[code].angle, chartRadius);
            const isTop3 = top3Codes.has(code);

            // Get score position for pulse animation
            const score = scores[code] || 0;
            const ratio = Math.min(score / maxScore, 1);
            const scoreRadius = chartRadius * ratio;
            const { x: scoreX, y: scoreY } = getCoords(hollandConfig[code].angle, scoreRadius);

            return (
              <g key={code}>
                {/* Axis line */}
                <line
                  x1={center}
                  y1={center}
                  x2={x}
                  y2={y}
                  stroke='#E5E7EB'
                  strokeWidth={1}
                  className='dark:stroke-gray-700'
                />

                {/* Animated pulse for top 3 - travels from badge to score dot */}
                {isTop3 && (
                  <circle
                    cx={0}
                    cy={0}
                    r={3}
                    fill='#dc2626'
                    className='animate-pulse-travel'
                    style={{
                      animationDelay: `${idx * 0.3}s`,
                    }}
                  >
                    <animateMotion
                      dur='2s'
                      repeatCount='indefinite'
                      begin={`${idx * 0.3}s`}
                      path={`M ${x} ${y} L ${scoreX} ${scoreY}`}
                    />
                  </circle>
                )}
              </g>
            );
          })}

          {/* User Score Polygon - matching Compass.tsx */}
          <polygon
            points={scorePolygonPoints}
            fill='rgba(249, 115, 22, 0.2)'
            stroke='#F97316'
            strokeWidth={3}
            strokeLinejoin='round'
            className='drop-shadow-md origin-center animate-grow-polygon'
          />

          {/* Score Points (Dots) - matching Compass.tsx */}
          {ORDERED_CODES.map((code, index) => {
            const score = scores[code] || 0;
            const ratio = Math.min(score / maxScore, 1);
            const { x, y } = getCoords(hollandConfig[code].angle, chartRadius * ratio);
            const isTop3 = top3Codes.has(code);

            return (
              <g key={code}>
                {/* Score dot with shadow */}
                <circle
                  cx={x}
                  cy={y}
                  r={isTop3 ? 6.5 : 4}
                  fill={isTop3 ? '#dc2626' : '#F97316'}
                  filter={isTop3 ? 'url(#shadow)' : undefined}
                  className='origin-center animate-pop-in'
                  style={{ animationDelay: `${index * 100 + 500}ms` }}
                />
                {/* White highlight on dot for 3D effect */}
                {isTop3 && (
                  <circle
                    cx={x - 1.5}
                    cy={y - 1.5}
                    r={2}
                    fill='white'
                    opacity={0.6}
                    className='animate-pop-in'
                    style={{ animationDelay: `${index * 100 + 550}ms` }}
                  />
                )}
              </g>
            );
          })}

          {/* SVG Filters for effects */}
          <defs>
            <filter id='shadow' x='-50%' y='-50%' width='200%' height='200%'>
              <feGaussianBlur in='SourceAlpha' stdDeviation='2' />
              <feOffset dx='0' dy='1' result='offsetblur' />
              <feComponentTransfer>
                <feFuncA type='linear' slope='0.5' />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
          </defs>
        </svg>

        {/* Nodes (Icons) - matching Compass.tsx */}
        {ORDERED_CODES.map((code, idx) => {
          const conf = hollandConfig[code];
          const Icon = conf.icon;
          const { x, y } = getCoords(conf.angle, iconRadius);
          const isTop3 = top3Codes.has(code);
          const score = scores[code] || 0;

          return (
            <div
              key={code}
              className='absolute flex flex-col items-center justify-center transition-all duration-300 cursor-pointer z-10'
              style={{
                left: x,
                top: y,
                transform: 'translate(-50%, -50%)',
              }}
            >
              {/* Hexagon Shape Background for Icon - matching Compass.tsx */}
              <div
                className='flex items-center justify-center rounded-2xl shadow-sm mb-2 transition-colors duration-300 border-2 bg-white/90 border-gray-100 dark:bg-gray-800/90 dark:border-gray-700'
                style={{
                  width: Math.max(32, 48 * scaleFactor),
                  height: Math.max(32, 48 * scaleFactor),
                }}
              >
                <Icon
                  className='transition-colors font-bold'
                  style={{
                    color: conf.color,
                    width: Math.max(16, 24 * scaleFactor),
                    height: Math.max(16, 24 * scaleFactor),
                  }}
                  strokeWidth={2.5}
                />
              </div>

              {/* Label - matching Compass.tsx */}
              <div className='flex flex-col items-center whitespace-nowrap'>
                <span
                  className='font-bold uppercase tracking-wider mb-0.5'
                  style={{
                    color: conf.color,
                    fontSize: `${Math.max(9, 12 * scaleFactor)}px`,
                  }}
                >
                  {conf.label}
                </span>
                {isTop3 ? (
                  <span
                    className='font-extrabold text-white px-2 py-0.5 rounded-full shadow-lg shadow-red-500/50 relative overflow-hidden transition-transform duration-300 hover:scale-110'
                    style={{
                      fontSize: `${Math.max(11, 15 * scaleFactor)}px`,
                      padding: scaleFactor < 0.7 ? '2px 6px' : '4px 10px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)',
                      backgroundSize: '200% 100%',
                    }}
                  >
                    <span className='relative z-10'>{score}đ</span>
                    {/* Shimmer effect - slides left to right along the badge */}
                    <span
                      className='absolute inset-0'
                      style={{
                        background:
                          'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: `shimmer 3s linear infinite`,
                        animationDelay: `${idx * 0.5}s`,
                      }}
                    />
                  </span>
                ) : (
                  <span
                    className='font-bold text-gray-500 dark:text-gray-400 transition-all duration-300'
                    style={{
                      fontSize: '11px',
                    }}
                  >
                    {score}đ
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Center Brain/User Icon - matching Compass.tsx */}
        <div
          className='absolute z-20 rounded-full bg-white dark:bg-gray-800 shadow-lg border-4 flex items-center justify-center'
          style={{
            left: center - Math.max(40, 64 * scaleFactor) / 2,
            top: center - Math.max(40, 64 * scaleFactor) / 2,
            width: Math.max(40, 64 * scaleFactor),
            height: Math.max(40, 64 * scaleFactor),
            borderColor: '#fff',
          }}
        >
          <div className='w-full h-full rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center'>
            <Brain
              className='text-white'
              style={{
                width: Math.max(20, 32 * scaleFactor),
                height: Math.max(20, 32 * scaleFactor),
              }}
            />
          </div>
        </div>

        {/* CSS Keyframes for animations */}
        <style>{`
          @keyframes growPolygon {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes popIn {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes float {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.02); }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .animate-grow-polygon {
            animation: growPolygon 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                       float 3s ease-in-out infinite 1s;
          }
          .animate-pop-in {
            opacity: 0;
            animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                       float 3s ease-in-out infinite 1s;
          }
          .animate-pulse-travel {
            opacity: 0.8;
          }
        `}</style>
      </div>
    </div>
  );
}

// Helper function to get color classes based on theme
const getColorClasses = (theme: 'yellow' | 'orange' | 'amber') => {
  const colorMap = {
    yellow: {
      gradient: 'from-yellow-500 to-yellow-600',
      ring: 'ring-yellow-100 dark:ring-yellow-900/50',
      text: 'text-yellow-600 dark:text-yellow-400',
      hoverRing: 'hover:ring-yellow-200 dark:hover:ring-yellow-800',
      quote: 'text-yellow-500',
      quoteOpacity: 'text-yellow-500/30 dark:text-yellow-400/30',
      usedSince: 'text-yellow-600 dark:text-yellow-400',
    },
    orange: {
      gradient: 'from-orange-500 to-orange-600',
      ring: 'ring-orange-100 dark:ring-orange-900/50',
      text: 'text-orange-600 dark:text-orange-400',
      hoverRing: 'hover:ring-orange-200 dark:hover:ring-orange-800',
      quote: 'text-orange-500',
      quoteOpacity: 'text-orange-500/30 dark:text-orange-400/30',
      usedSince: 'text-orange-600 dark:text-orange-400',
    },
    amber: {
      gradient: 'from-amber-500 to-amber-600',
      ring: 'ring-amber-100 dark:ring-amber-900/50',
      text: 'text-amber-600 dark:text-amber-400',
      hoverRing: 'hover:ring-amber-200 dark:hover:ring-amber-800',
      quote: 'text-amber-500',
      quoteOpacity: 'text-amber-500/30 dark:text-amber-400/30',
      usedSince: 'text-amber-600 dark:text-amber-400',
    },
  };
  return colorMap[theme];
};

// Helper function to format content with bold text
const formatContent = (content: string): ReactNode[] => {
  const parts = content.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return (
        <strong key={index} className='text-gray-900 dark:text-white'>
          {part}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export function LandingPage() {
  // Generate random scores on mount - range 2-6
  const [demoScores] = useState(() => generateRandomScores());

  // Inject FAQ structured data for SEO
  useEffect(() => {
    injectFAQStructuredData(CAT_FAQ);
  }, []);

  // Track scroll depth
  useEffect(() => {
    return initScrollDepthTracking();
  }, []);

  return (
    <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Hướng nghiệp CAT | Thi thử TN THPT | ĐGNL bản quyền IEQTE'
        description='Trắc nghiệm hướng nghiệp CAT kết hợp bài thi thử TN THPT và phương pháp khoa học ĐGNL có bản quyền của IEQTE, giúp học sinh hiểu rõ bản thân, lựa chọn ngành học phù hợp và xây dựng bản đồ nghề nghiệp hiệu quả, bền vững.'
        keywords='hướng nghiệp CAT, trắc nghiệm hướng nghiệp, CAT test, định hướng nghề nghiệp, thi thử THPT, chọn ngành đại học, hướng nghiệp THPT, ĐGNL, career aptitude test'
        canonicalUrl={getCanonicalUrl('/')}
      />
      <StructuredData
        data={{
          type: 'WebApplication',
          name: 'Hướng nghiệp CAT',
          description:
            'Nền tảng trắc nghiệm hướng nghiệp và thi thử THPT trực tuyến - Phương pháp khoa học ĐGNL bản quyền IEQTE',
          applicationCategory: 'EducationalApplication',
          operatingSystem: 'All',
          offers: { price: '0', priceCurrency: 'VND' },
        }}
      />
      <UnifiedHeader />
      <motion.main
        className='flex-1 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Hero Section - lines 72-133 */}
        <section className='relative overflow-hidden bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-background-dark pt-16 pb-20 lg:pt-24 lg:pb-28'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
              <div className='text-center lg:text-left'>
                <span className='inline-block py-1 px-3 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs font-bold tracking-wide uppercase mb-4'>
                  Hướng nghiệp CAT - Career Aptitude Test
                </span>
                <h1 className='text-2xl lg:text-5xl font-extrabold tracking-tight mb-6 leading-tight'>
                  <span className='block text-gray-900 dark:text-white'>Hướng nghiệp CAT</span>
                  <span className='block text-yellow-600 dark:text-yellow-400'>
                    Hiểu mình hôm nay, vững nghề mai sau
                  </span>
                </h1>
                <p className='mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-lg mx-auto lg:mx-0 mb-8'>
                  Công cụ trắc nghiệm hướng nghiệp CAT đã{' '}
                  <span className='font-bold'>đăng ký bản quyền</span>, thử nghiệm trên{' '}
                  <span className='font-bold'>diện rộng tại Việt Nam</span>. Bạn hãy làm thử bài thi
                  tốt nghiệp THPT và kết hợp với kết quả CAT để xây dựng hồ sơ năng lực phục vụ cho
                  việc định hướng nghề nghiệp, và xét tuyển đại học.
                </p>
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl relative z-30 mx-auto lg:mx-0'>
                  <Button size='lg' className='uppercase w-full' asChild>
                    <Link
                      to='/ho-so-nang-luc'
                      onClick={() => trackCTAClick('ho_so_nang_luc', 'hero')}
                    >
                      Hồ sơ năng lực
                    </Link>
                  </Button>
                  <Button size='lg' variant='outline' className='uppercase w-full' asChild>
                    <Link
                      to='/huong-nghiep-cat?screen=instruction'
                      onClick={() => trackCTAClick('lam_bai_cat', 'hero')}
                    >
                      Làm bài CAT
                    </Link>
                  </Button>
                  <Button size='lg' variant='outline' className='uppercase w-full' asChild>
                    <Link to='/thi-thu-thpt' onClick={() => trackCTAClick('thi_thu_thpt', 'hero')}>
                      Thi thử THPT
                    </Link>
                  </Button>
                </div>
              </div>
              <div className='relative mx-auto w-full max-w-lg lg:max-w-none'>
                <div className='relative rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700'>
                  <div className='absolute top-0 left-0 w-full h-8 bg-gray-100 dark:bg-gray-700 flex items-center px-4 space-x-2 border-b border-gray-200 dark:border-gray-600'>
                    <div className='w-3 h-3 rounded-full bg-red-400'></div>
                    <div className='w-3 h-3 rounded-full bg-yellow-400'></div>
                    <div className='w-3 h-3 rounded-full bg-green-400'></div>
                  </div>
                  <div className='pt-8 overflow-hidden'>
                    <HollandRadarChart scores={demoScores} />
                  </div>
                </div>
                <div className='absolute -bottom-6 -left-6 w-40 h-40 lg:w-52 lg:h-52 z-20 hidden sm:block'>
                  <img
                    alt='Student studying'
                    className='w-full h-full object-cover rounded-full border-8 border-white dark:border-gray-800 shadow-xl'
                    src='/students-studying.png'
                    width='256'
                    height='256'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CAT DÀNH CHO AI? - lines 134-170 */}
        <section className='py-20 bg-surface-light dark:bg-surface-dark'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center mb-16'>
              <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                HƯỚNG NGHIỆP CAT DÀNH CHO AI?
              </h2>
              <div className='w-20 h-1 bg-primary mx-auto rounded-full'></div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
              <div className='bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 transition-all duration-300 group'>
                <div className='w-14 h-14 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <span className='material-icons-outlined text-3xl text-blue-600 dark:text-blue-400'>
                    school
                  </span>
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  Học sinh THPT
                </h3>
                <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                  Bài kiểm tra năng lực toàn diện kết hợp <strong>trắc nghiệm tâm lý</strong>, giúp
                  học sinh nhận diện điểm mạnh và thiên hướng nghề nghiệp.
                </p>
              </div>
              <div className='bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 transition-all duration-300 group'>
                <div className='w-14 h-14 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <span className='material-icons-outlined text-3xl text-yellow-600 dark:text-yellow-400'>
                    family_restroom
                  </span>
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>Phụ huynh</h3>
                <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                  <strong>Thấu hiểu và đồng hành</strong> cùng con trong giai đoạn chọn ngành quan
                  trọng, dựa trên các dữ liệu đánh giá khoa học và khách quan.
                </p>
              </div>
              <div className='bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 transition-all duration-300 group'>
                <div className='w-14 h-14 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <span className='material-icons-outlined text-3xl text-green-600 dark:text-green-400'>
                    work_outline
                  </span>
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  Cố vấn nghề nghiệp
                </h3>
                <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                  Công cụ để chuyên gia tư vấn nghề nghiệp có cái nhìn tổng quan định hướng việc làm
                  cho học sinh.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CAT HOẠT ĐỘNG NHƯ THẾ NÀO? - lines 171-210 */}
        <section className='py-20 bg-yellow-50 dark:bg-gray-900'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center mb-16'>
              <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                HƯỚNG NGHIỆP CAT HOẠT ĐỘNG NHƯ THẾ NÀO?
              </h2>
              <div className='w-20 h-1 bg-primary mx-auto rounded-full'></div>
            </div>
            <div className='relative'>
              <div className='hidden md:block absolute top-10 left-0 w-full h-0.5 bg-gray-300 dark:bg-gray-700 -z-0'></div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-10'>
                <div className='relative z-10 flex flex-col items-center text-center'>
                  <div className='w-20 h-20 rounded-full bg-white dark:bg-gray-800 border-4 border-yellow-100 dark:border-yellow-900 flex items-center justify-center shadow-lg mb-6'>
                    <span className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                      1.
                    </span>
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-3'>
                    Thực hiện bài test trực tuyến
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xs'>
                    Hoàn thành bộ câu hỏi trắc nghiệm được thiết kế tối ưu hóa thời gian, thao tác
                    đơn giản trên mọi thiết bị.
                  </p>
                </div>
                <div className='relative z-10 flex flex-col items-center text-center'>
                  <div className='w-20 h-20 rounded-full bg-white dark:bg-gray-800 border-4 border-yellow-100 dark:border-yellow-900 flex items-center justify-center shadow-lg mb-6'>
                    <span className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                      2.
                    </span>
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-3'>
                    Hệ thống phân tích tự động
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xs'>
                    Ứng dụng các thuật toán AI thông minh để xử lý dữ liệu, đánh giá sâu chuỗi sở
                    thích và năng lực của bạn.
                  </p>
                </div>
                <div className='relative z-10 flex flex-col items-center text-center'>
                  <div className='w-20 h-20 rounded-full bg-white dark:bg-gray-800 border-4 border-yellow-100 dark:border-yellow-900 flex items-center justify-center shadow-lg mb-6'>
                    <span className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                      3.
                    </span>
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-3'>
                    Nhận kết quả &amp; Gợi ý ngành
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xs'>
                    Nhận báo cáo chi tiết và danh sách ngành nghề phù hợp nhất gửi trực tiếp về
                    email của bạn ngay lập tức.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BẠN NHẬN ĐƯỢC GÌ TỪ CAT - lines 211-310 */}
        <section className='py-20 bg-surface-light dark:bg-surface-dark'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center mb-16'>
              <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                BẠN NHẬN ĐƯỢC GÌ TỪ HƯỚNG NGHIỆP CAT
              </h2>
              <div className='w-20 h-1 bg-primary mx-auto rounded-full'></div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12'>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0'>
                  <div className='w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400'>
                    <span className='material-icons-outlined'>psychology</span>
                  </div>
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Hiểu sâu về năng lực cốt lõi
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Hiểu sâu về năng lực cốt lõi, trắc nghiệm xác nhận tiềm năng và hiểu về năng lực
                    cốt lõi.
                  </p>
                </div>
              </div>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0'>
                  <div className='w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400'>
                    <span className='material-icons-outlined'>lightbulb</span>
                  </div>
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Gợi ý ngành nghề phù hợp
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Gợi ý ngành nghề phù hợp, gợi ý ngành nghề mà công cụ phụ hiện công minh.
                  </p>
                </div>
              </div>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0'>
                  <div className='w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400'>
                    <span className='material-icons-outlined'>trending_up</span>
                  </div>
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Kế hoạch phát triển cá nhân
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Xây dựng lộ trình phát triển cá nhân dựa trên năng lực và mục tiêu nghề nghiệp
                    của bạn.
                  </p>
                </div>
              </div>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0'>
                  <div className='w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400'>
                    <span className='material-icons-outlined'>groups</span>
                  </div>
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Kho dữ liệu ngành nghề đa dạng
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Cung cấp thông tin chi tiết, yêu cầu và cơ hội việc làm của hàng ngàn ngành nghề
                    hiện đại.
                  </p>
                </div>
              </div>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0'>
                  <div className='w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400'>
                    <span className='material-icons-outlined'>school</span>
                  </div>
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Định hướng giáo dục tiếp theo
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Gợi ý các chương trình đào tạo, trường đại học phù hợp với năng lực của bạn.
                  </p>
                </div>
              </div>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0'>
                  <div className='w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400'>
                    <span className='material-icons-outlined'>assignment_turned_in</span>
                  </div>
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Mục tiêu rõ ràng, động lực vững vàng
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Tự tin hơn vào con đường nghề nghiệp khi biết rõ bản thân mình.
                  </p>
                </div>
              </div>
            </div>
            <div className='mt-16 bg-yellow-50 dark:bg-gray-800 rounded-2xl p-8 flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8'>
              <div className='flex-shrink-0'>
                <div className='w-24 h-24 rounded-full bg-white dark:bg-gray-700 flex items-center justify-center shadow-sm'>
                  <span className='material-icons-outlined text-5xl text-yellow-600 dark:text-yellow-400'>
                    verified_user
                  </span>
                </div>
              </div>
              <div className='text-center md:text-left'>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                  BẢN QUYỀN & CHẤT LƯỢNG KHOA HỌC
                </h3>
                <p className='text-lg font-medium text-gray-800 dark:text-gray-200 mb-1'>
                  Sản phẩm của Viện Khảo thí và Đánh giá Chất lượng Giáo dục. Đã được Cục Bản quyền
                  tác giả chứng nhận (14/09/2024). Xây dựng dựa trên nghiên cứu khoa học và thử
                  nghiệm trên diện rộng học sinh Việt Nam.
                </p>
                <div className='mt-3 flex items-center justify-center md:justify-start relative group cursor-help w-fit mx-auto md:mx-0'>
                  <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800 z-10 relative'>
                    <span className='material-icons-outlined text-sm mr-1'>verified</span>
                    Đã chứng nhận bản quyền
                  </span>
                  {/* Hover Image Popup */}
                  <div className='absolute left-1/2 -translate-x-1/2 bottom-full mb-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 w-64 md:w-80'>
                    <div className='bg-white dark:bg-gray-800 p-2 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700'>
                      <img
                        src='/giay-chung-nhan-ban-quyen.jpg'
                        alt='Giấy chứng nhận đăng ký quyền tác giả'
                        className='w-full h-auto rounded'
                        loading='lazy'
                        decoding='async'
                        width='320'
                        height='240'
                      />
                      <div className='absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-4 h-4 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700'></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KHÁM PHÁ CÁC TÍNH NĂNG NỔI BẬT */}
        <section className='py-20 bg-white dark:bg-gray-900'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center mb-16'>
              <span className='inline-block py-1 px-3 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs font-bold tracking-wide uppercase mb-4'>
                Tính năng nổi bật
              </span>
              <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                KHÁM PHÁ HỆ SINH THÁI HƯỚNG NGHIỆP
              </h2>
              <p className='text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
                Ba công cụ mạnh mẽ giúp bạn hiểu bản thân, luyện thi hiệu quả và chọn ngành phù hợp
              </p>
              <div className='w-20 h-1 bg-primary mx-auto rounded-full mt-4'></div>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8'>
              {/* Feature 1: Hướng nghiệp CAT */}
              <div className='group relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-8 border border-blue-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300'>
                <div className='absolute top-4 right-4'>
                  <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'>
                    Miễn phí
                  </span>
                </div>
                <div className='w-16 h-16 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <Brain className='w-8 h-8 text-blue-600 dark:text-blue-400' />
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  Khám phá bản thân
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4'>
                  Bài trắc nghiệm <strong>CAT (Career Aptitude Test)</strong> dựa trên mô hình
                  Holland giúp bạn hiểu rõ sở thích, tính cách và xu hướng nghề nghiệp trong{' '}
                  <strong>chỉ 5 phút</strong>.
                </p>
                <div className='space-y-2 mb-6'>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>48 câu hỏi trắc nghiệm nhanh</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Phân tích 6 nhóm tính cách RIASEC</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Gợi ý ngành học cá nhân hóa</span>
                  </div>
                </div>
                <Link
                  to='/huong-nghiep-cat?screen=instruction'
                  className='inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold text-sm hover:underline'
                  onClick={() => trackCTAClick('bat_dau_cat', 'feature_card')}
                >
                  Bắt đầu ngay
                  <span className='material-icons-outlined text-base'>arrow_forward</span>
                </Link>
              </div>

              {/* Feature 2: Luyện đề THPT */}
              <div className='group relative bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-8 border border-yellow-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300'>
                <div className='absolute top-4 right-4'>
                  <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'>
                    Hot 🔥
                  </span>
                </div>
                <div className='w-16 h-16 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <BookOpen className='w-8 h-8 text-yellow-600 dark:text-yellow-400' />
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  Luyện đề THPT
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4'>
                  Thi thử các môn <strong>Toán, Văn, Anh, Lý, Hóa...</strong> với đề thi chuẩn
                  format kỳ thi Tốt nghiệp THPT. Mô phỏng áp lực thời gian thật, phân tích điểm mạnh
                  - yếu.
                </p>
                <div className='space-y-2 mb-6'>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>100+ đề thi từ các năm trước</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Đếm ngược thời gian như thật</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Xem điểm ngay sau khi nộp bài</span>
                  </div>
                </div>
                <Link
                  to='/thi-thu-thpt'
                  className='inline-flex items-center gap-2 text-yellow-600 dark:text-yellow-400 font-semibold text-sm hover:underline'
                  onClick={() => trackCTAClick('thi_thu_thpt', 'feature_card')}
                >
                  Thi thử ngay
                  <span className='material-icons-outlined text-base'>arrow_forward</span>
                </Link>
              </div>

              {/* Feature 3: Khám phá Ngành học */}
              <div className='group relative bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-8 border border-green-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300'>
                <div className='absolute top-4 right-4'>
                  <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'>
                    100+ ngành
                  </span>
                </div>
                <div className='w-16 h-16 rounded-xl bg-green-100 dark:bg-green-900/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <GraduationCap className='w-8 h-8 text-green-600 dark:text-green-400' />
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  Khám phá Ngành học
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4'>
                  Tìm hiểu chi tiết <strong>100+ ngành học đại học</strong> tại Việt Nam với thông
                  tin về cơ hội việc làm, mức lương, trường đào tạo và khối thi xét tuyển.
                </p>
                <div className='space-y-2 mb-6'>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Phân loại theo 6 nhóm RIASEC</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Thông tin lương, nghề nghiệp hot</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Liên kết với kết quả CAT của bạn</span>
                  </div>
                </div>
                <Link
                  to='/nganh-hoc'
                  className='inline-flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold text-sm hover:underline'
                  onClick={() => trackCTAClick('xem_nganh_hoc', 'feature_card')}
                >
                  Xem danh sách ngành
                  <span className='material-icons-outlined text-base'>arrow_forward</span>
                </Link>
              </div>

              {/* Feature 4: Hồ Sơ Năng Lực */}
              <div className='group relative bg-gradient-to-br from-sky-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-8 border border-sky-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300'>
                <div className='absolute top-4 right-4'>
                  <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300'>
                    Mới ✨
                  </span>
                </div>
                <div className='w-16 h-16 rounded-xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                  <FileText className='w-8 h-8 text-sky-600 dark:text-sky-400' />
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  Hồ Sơ Năng Lực
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4'>
                  Kết hợp <strong>kết quả trắc nghiệm CAT</strong> và <strong>điểm thi THPT</strong>{' '}
                  để gợi ý ngành đại học phù hợp nhất với năng lực và sở thích của bạn.
                </p>
                <div className='space-y-2 mb-6'>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Gợi ý nhóm ngành học phù hợp dựa trên năng lực và tính cách</span>
                  </div>
                  <div className='flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-base'>
                      check_circle
                    </span>
                    <span>Tải PDF hồ sơ năng lực cá nhân</span>
                  </div>
                </div>
                <Link
                  to='/ho-so-nang-luc'
                  className='inline-flex items-center gap-2 text-sky-600 dark:text-sky-400 font-semibold text-sm hover:underline'
                  onClick={() => trackCTAClick('xem_ho_so', 'feature_card')}
                >
                  Xem hồ sơ của bạn
                  <span className='material-icons-outlined text-base'>arrow_forward</span>
                </Link>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className='mt-12 text-center'>
              <p className='text-gray-600 dark:text-gray-400 mb-4'>
                Bắt đầu hành trình khám phá bản thân và xây dựng tương lai ngay hôm nay!
              </p>
              <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
                <Button size='lg' className='uppercase' asChild>
                  <Link
                    to='/huong-nghiep-cat?screen=instruction'
                    onClick={() => trackCTAClick('lam_bai_cat', 'bottom_cta')}
                  >
                    LÀM BÀI CAT MIỄN PHÍ
                  </Link>
                </Button>
                <Button size='lg' variant='outline' className='uppercase' asChild>
                  <Link
                    to='/thi-thu-thpt'
                    onClick={() => trackCTAClick('thi_thu_thpt', 'bottom_cta')}
                  >
                    THI THỬ NGAY
                  </Link>
                </Button>
                <Button size='lg' variant='outline' className='uppercase' asChild>
                  <Link
                    to='/ho-so-nang-luc'
                    onClick={() => trackCTAClick('ho_so_nang_luc', 'bottom_cta')}
                  >
                    HỒ SƠ NĂNG LỰC
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className='py-20 bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 dark:from-gray-900 dark:via-yellow-900/20 dark:to-orange-900/20 relative overflow-hidden'>
          {/* Decorative background elements */}
          <div className='absolute top-0 right-0 w-96 h-96 bg-yellow-200/20 dark:bg-yellow-800/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2'></div>
          <div className='absolute bottom-0 left-0 w-96 h-96 bg-orange-200/20 dark:bg-orange-800/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2'></div>

          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
            <div className='text-center mb-16'>
              <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 mb-4 shadow-lg'>
                <span className='material-icons-outlined text-white text-3xl'>format_quote</span>
              </div>
              <h2 className='text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4'>
                CHIA SẺ TỪ NGƯỜI ĐÃ SỬ DỤNG
              </h2>
              <p className='text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-2'>
                Những câu chuyện thực tế từ phụ huynh, sinh viên và thầy cô đã trải nghiệm Hướng
                Nghiệp CAT
              </p>
              <div className='w-20 h-1 bg-gradient-to-r from-yellow-500 to-orange-500 mx-auto rounded-full'></div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
              {TESTIMONIALS.map(testimonial => {
                const colors = getColorClasses(testimonial.colorTheme);
                const contentLines = testimonial.content.split('\n').filter(line => line.trim());

                return (
                  <div
                    key={testimonial.id}
                    className={`group hover:shadow-2xl transition-all duration-300 hover:ring-2 ${colors.hoverRing} bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 flex flex-col relative overflow-hidden`}
                  >
                    {/* Quote icon decoration */}
                    <div className='absolute top-4 right-4 opacity-10'>
                      <span className={`material-icons-outlined text-6xl ${colors.quote}`}>
                        format_quote
                      </span>
                    </div>

                    <div className='relative z-10'>
                      <div className='flex items-center gap-4 mb-6'>
                        <div
                          className={`w-16 h-16 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white font-bold text-xl shadow-lg ring-4 ${colors.ring}`}
                        >
                          <span className='material-icons-outlined text-3xl'>
                            {testimonial.icon}
                          </span>
                        </div>
                        <div className='flex-1'>
                          <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
                            {testimonial.name}
                          </h3>
                          <p className={`text-sm font-medium ${colors.text}`}>{testimonial.role}</p>
                          <p className='text-xs text-gray-500 dark:text-gray-500 mt-1'>
                            {testimonial.location} • {testimonial.age} tuổi
                          </p>
                        </div>
                      </div>

                      <div className='flex items-center gap-1 mb-4'>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <span
                            key={i}
                            className='material-icons-outlined text-yellow-400 text-lg drop-shadow-sm'
                          >
                            star
                          </span>
                        ))}
                      </div>

                      <div className='relative'>
                        <p className='text-gray-700 dark:text-gray-300 leading-relaxed text-sm flex-1'>
                          <span
                            className={`text-3xl ${colors.quoteOpacity} font-serif leading-none mr-1`}
                          >
                            "
                          </span>
                          {contentLines.map((line, index) => {
                            // Check if line contains bold markers
                            if (line.includes('**')) {
                              return (
                                <span key={index}>
                                  {formatContent(line)}
                                  {index < contentLines.length - 1 && (
                                    <>
                                      <br />
                                      <br />
                                    </>
                                  )}
                                </span>
                              );
                            }
                            return (
                              <span key={index}>
                                {line}
                                {index < contentLines.length - 1 && (
                                  <>
                                    <br />
                                    <br />
                                  </>
                                )}
                              </span>
                            );
                          })}
                          <span
                            className={`text-3xl ${colors.quoteOpacity} font-serif leading-none ml-1`}
                          >
                            "
                          </span>
                        </p>
                      </div>

                      <div className='mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2'>
                        <span className='text-xs text-gray-500 dark:text-gray-400'>
                          Đã sử dụng CAT
                        </span>
                        <span className={`${colors.usedSince} font-semibold text-xs`}>
                          {testimonial.usedSince}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* THPT Exam Practice - lines 311-325 */}
        <section className='py-16 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 border-t border-gray-200 dark:border-gray-700'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center mb-10'>
              <h3 className='text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                LUYỆN THI & ĐÁNH GIÁ NĂNG LỰC TOÀN DIỆN
              </h3>
              <div className='w-24 h-1 bg-primary mx-auto rounded-full'></div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
              {/* Cột 1: Thi thử như thật */}
              <div className='bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow'>
                <h4 className='text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center'>
                  <span className='material-icons-outlined text-primary mr-2'>timer</span>
                  Thi thử như thật
                </h4>
                <ul className='space-y-3'>
                  <li className='flex items-start text-gray-600 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                      check_circle
                    </span>
                    <span>Mô phỏng áp lực thời gian thực tế.</span>
                  </li>
                  <li className='flex items-start text-gray-600 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                      check_circle
                    </span>
                    <span>Nhận diện rõ điểm mạnh - điểm yếu kiến thức.</span>
                  </li>
                  <li className='flex items-start text-gray-600 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                      check_circle
                    </span>
                    <span>Chuẩn bị sẵn sàng cho kỳ thi trên máy tính sắp tới.</span>
                  </li>
                </ul>
              </div>

              {/* Cột 2: Tại sao cần kết hợp */}
              <div className='bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow'>
                <h4 className='text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center'>
                  <span className='material-icons-outlined text-primary mr-2'>model_training</span>
                  Tại sao cần kết hợp với CAT?
                </h4>
                <ul className='space-y-3'>
                  <li className='flex items-start text-gray-600 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                      check_circle
                    </span>
                    <span>
                      Kết hợp <strong className='text-gray-800 dark:text-white'>Sở thích</strong>{' '}
                      (CAT) +{' '}
                      <strong className='text-gray-800 dark:text-white'>Năng lực học tập</strong>{' '}
                      (Thi thử) = Tư vấn sát thực tế nhất.
                    </span>
                  </li>
                  <li className='flex items-start text-gray-600 dark:text-gray-300'>
                    <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                      check_circle
                    </span>
                    <span>Khuyến nghị thi lặp lại 3 lần để đảm bảo độ ổn định của kết quả.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className='text-center'>
              <Button size='lg' className='uppercase' asChild>
                <Link
                  to='/thi-thu-thpt'
                  onClick={() => trackCTAClick('thi_thu_thpt', 'exam_info_section')}
                >
                  LÀM BÀI THI THỬ NGAY
                  <span className='material-icons-outlined ml-2'>arrow_forward</span>
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </motion.main>

      {/* FAQ Section - SEO Enhancement - Lazy Loaded */}
      <Suspense fallback={<div className='py-16 text-center'>Đang tải...</div>}>
        <FAQSection className='bg-gray-50 dark:bg-gray-900' />
      </Suspense>

      {/* Lazy load Footer to reduce initial bundle size */}
      <Suspense fallback={null}>
        <UnifiedFooter />
      </Suspense>
    </div>
  );
}
